import { ISidebarNotification } from 'components/Sidebar/components/Sidebar';
import { Appearance } from 'design';
import { CalculatorSettingsChoice, DisbursementMethod, EmployeeRole, IntegrationCategory, OnboardingEmployeeStatus, PayoutApprovalStatus, PayoutStatus, SettingsGateState } from 'types';

/* HTTP Status Codes  */
export enum HTTPStatusCode {
  PARTIAL_CONTENT = '206',
  UNAUTHORIZED = '401',
  NOT_FOUND = '404',
  CONFLICT = '409',
  UNPROCESSABLE_ENTITY = '422',
}

/* Org Admin Default Job Title */
export const DEFAULT_ORG_ADMIN_JOB_TITLE = 'org admin';

/* Toast Message */
export const GENERIC_TOAST_ERROR_MESSAGE = 'Sorry, an unexpected error occurred. If this problem continues, please contact Kickfin Support.';

/* Fallback copy for the 409 `client_capabilities_required` refresh prompt.
   The backend normally supplies its own `msg`; this is only used if that message is missing. */
export const CLIENT_CAPABILITIES_REFRESH_MESSAGE = 'Please refresh and try again.';

/* Oauth Key for Connecting an Integration */
export const CONNECT_OAUTH_KEY = 'integration_oauth_state';

/* POS integrations categories (labor/ticket); payroll/HCM are excluded. */
export const POS_CATEGORIES = [IntegrationCategory.LABOR, IntegrationCategory.TICKET];

/* Mobile Locale */
export const MOBILE_LOCALE = 'en-US';

/* Default Backdate Limit (in days) */
export const DEFAULT_BACKDATE_LIMIT = 7;

/* Feature flag polling intervals (ms). Baseline keeps clients aware of slow changes;
   active kicks in when maintenance_mode is enabled so users see deploy steps land quickly. */
export const FEATURE_FLAG_POLL_INTERVAL_BASELINE_MS = 60 * 60 * 1000;
export const FEATURE_FLAG_POLL_INTERVAL_MAINTENANCE_MS = 60 * 1000;

/* State Abbreviations */ // prettier-ignore
export const STATE_ABVS: string[] = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA',
  'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR',
  'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'AS', 'GU', 'MP', 'PR', 'VI', 'FM',
  'MH', 'PW', 'AA', 'AE', 'AP',
];

/* Dropdown option type */
export type DDOption = { value: any; label: string };

/* Site Role Options */
export const SiteRoleOptions: Array<DDOption> = [
  { value: EmployeeRole.SITE_ADMIN, label: 'Site Admin' },
  { value: EmployeeRole.MANAGER, label: 'Manager' },
  { value: EmployeeRole.EMPLOYEE, label: 'Employee' },
];

/* Admin Role Options */
export const AdminRoleOptions: Array<DDOption> = [{ value: 'admin', label: 'Organization Admin' }];

/* User Role Options */
export const UserRoleOptions: Array<DDOption> = [...AdminRoleOptions, ...SiteRoleOptions];

/* Dismiss Reason Options */
export const DismissReasons: Array<DDOption> = [
  { label: 'Seasonal Worker', value: 'Seasonal Worker' },
  { label: 'Site Transfer', value: 'Site Transfer' },
  { label: 'Staff Reduction', value: 'Staff Reduction' },
  { label: 'Employment Ended', value: 'Employment Ended' },
  { label: 'Other', value: 'Other' },
];

/* Disbursement Method Options */
export const DisbursementOptions: Array<DDOption> = [
  { label: 'Instant', value: DisbursementMethod.INSTANT },
  { label: 'Payroll', value: DisbursementMethod.PAYROLL },
];

export const ChangeDisbursementOptions: Array<DDOption> = [
  { label: 'Payroll', value: DisbursementMethod.PAYROLL },
  { label: 'Cash', value: DisbursementMethod.CASH },
];

/* Payout Status Filter Options */
export const PayoutStatusFilterOptions: Array<DDOption> = [
  { label: 'Paid', value: PayoutStatus.PAID },
  { label: 'Payroll', value: PayoutStatus.PAYROLL },
  { label: 'Cash', value: PayoutStatus.CASH },
  { label: 'Pending', value: PayoutStatus.PENDING },
  { label: 'Expired', value: PayoutStatus.EXPIRED },
  { label: 'Canceled', value: PayoutStatus.CANCELED },
];

/* Payout Resolution (approval) Filter Options */
export const PayoutResolutionOptions: Array<DDOption> = [
  { label: 'Approved', value: PayoutApprovalStatus.REVIEWER_APPROVED },
  { label: 'Rejected', value: PayoutApprovalStatus.REVIEWER_REJECTED },
  { label: 'Review Required', value: PayoutApprovalStatus.REVIEW_REQUIRED },
];

type MakePaymentNotificationConfig = {
  [key: string]: ISidebarNotification | MakePaymentNotificationConfig;
};

const defineMakePaymentNotification = <T extends MakePaymentNotificationConfig>(notifications: T) => notifications;

export const MakePaymentNotification = defineMakePaymentNotification({
  tipCalculator: {
    noPayees: {
      title: 'No Payees',
      message: 'There are no eligible payees for this date.',
      appearance: Appearance.DANGER,
      preventSubmit: true,
    },
    paymentAmountExceeded: {
      title: 'Payment Amount Exceeded',
      message: "One or more of your employees has a payment amount that exceeds the site's maximum payment amount.",
      appearance: Appearance.WARNING,
      preventSubmit: true,
    },
    dateAlreadyPaid: {
      title: 'Already Paid',
      message: 'You have already issued tips for this day.',
      appearance: Appearance.WARNING,
      preventSubmit: true,
    },
    userSetupNotComplete: {
      title: 'User Setup Not Complete',
      message: 'Please review and complete setup for all users.',
      appearance: Appearance.WARNING,
      preventSubmit: true,
    },
    ineligibleSelfPay: {
      title: 'Self Payment Restricted',
      message: 'The site role policies restrict payment to yourself.',
      appearance: Appearance.WARNING,
      preventSubmit: true,
    },
    ineligiblePayees: {
      title: 'Ineligible Payees',
      message: 'The following users are ineligible to receive tips due to their selected role:',
      appearance: Appearance.WARNING,
      preventSubmit: true,
    },
    safeMode: {
      title: 'Safe Mode',
      message: 'Payments cannot be submitted while the tip calculator is in Safe Mode.',
      appearance: Appearance.WARNING,
      preventSubmit: true,
    },
    cappedWarnings: {
      title: 'Tip Share Amounts Capped',
      message: "Some tip share amounts have been capped because there weren't enough tips or autograts collected to fully fund the sharing rules.",
      appearance: Appearance.WARNING,
      preventSubmit: false,
    },
    unallocatedFundsWarnings: {
      title: 'Unallocated Tips',
      message: 'Some tips were not allocated to any payees. If you proceed, these tips will not be paid by Kickfin.',
      appearance: Appearance.WARNING,
      preventSubmit: false,
    },
    negativeNetPayment: {
      title: 'This Payment Cannot Be Made',
      message: 'Please adjust the payments for the affected employees to resolve.',
      appearance: Appearance.WARNING,
      preventSubmit: true,
    },
    noTipsToBePaid: {
      title: 'No Tips To Be Paid',
      message: 'There are currently no collected tips or auto-gratuities to be paid.',
      appearance: Appearance.WARNING,
      preventSubmit: true,
    },
    allChecksIgnored: {
      title: 'All Checks Ignored',
      message: 'All checks with tips & autograts have been ignored. Please select “Undo Ignore” on at least one check in order to proceed with making a payment.',
      appearance: Appearance.WARNING,
      preventSubmit: true,
    },
  },
  upload: {
    ineligibleMatch: {
      title: 'Ineligible Match',
      message: 'You are not allowed to issue tips to one or more matched users. You can select a payable match or remove them in order to process this payment.',
      appearance: Appearance.DANGER,
      preventSubmit: true,
    },
    matchNotFound: {
      title: 'Match Not Found',
      message: 'One or more users were not matched with a Kickfin account. You can select a match or invite the employee to Kickfin in order to process this payment.',
      appearance: Appearance.DANGER,
      preventSubmit: true,
    },
    multipleMatchesFound: {
      title: 'Multiple Matches Found',
      message: 'Multiple Kickfin account matches have been found for one or more users. You can select a match or invite the correct employee to Kickfin in order to process this payment.',
      appearance: Appearance.DANGER,
      preventSubmit: true,
    },
  },
  duplicatePayment: {
    title: 'Duplicate Payment Set Found',
    message: 'A payment with these exact employees and amounts has already been made for this date. Please verify existing payment sets before proceeding.',
    appearance: Appearance.DANGER,
    preventSubmit: false,
  },
  payPeriodClosed: {
    title: 'Pay Period Closed',
    message: "Payments can't be submitted for this period. To submit, an Org Admin must reopen it first.",
    appearance: Appearance.WARNING,
    preventSubmit: true,
  },
  paymentsDisabled: {
    title: 'Payments Disabled',
    message: 'Payments are disabled for this site. You can not submit payments at this time.',
    appearance: Appearance.DANGER,
    preventSubmit: true,
  },
  permissionDenied: {
    title: 'Permission Denied',
    message: 'You do not have permissions to submit payments. Please contact Kickfin support.',
    appearance: Appearance.WARNING,
    preventSubmit: true,
  },
  payeeError: {
    title: 'Payees Error',
    message: 'One or more of your employees are ineligible to receive tips due to their selected role.',
    appearance: Appearance.DANGER,
    preventSubmit: true,
  },
  overdraftWarning: {
    title: 'Overdraft Warning',
    message: 'The site is overdrawn. You can not submit payments at this time.',
    appearance: Appearance.WARNING,
    preventSubmit: true,
  },
  payoutLimitExceeded: {
    title: 'Payout Limit Exceeded',
    message: 'You have exceeded the payout limit. You can not submit payments at this time.',
    appearance: Appearance.WARNING,
    preventSubmit: true,
  },
  payoutLimitNotMet: {
    title: 'Payout Limit Not Met',
    message: 'There is a remaining payout limit. Increase your payouts or decrease the limit.',
    appearance: Appearance.WARNING,
    preventSubmit: true,
  },
  systemError: {
    title: 'System Error',
    message: 'An error occurred while submitting payments. Please try again.',
    appearance: Appearance.DANGER,
    preventSubmit: true,
  },
});

/*
 * Copy for the calculator settings gate (KICK-5143), shown before recalculating an already-paid date.
 *
 * Kept alongside `MakePaymentNotification` rather than inside it: every member of that map is an
 * `ISidebarNotification` pushed into a sidebar notification list, so it requires an `appearance` and a
 * `preventSubmit` that mean nothing to a modal.
 */
interface ISettingsGateCopy {
  title: string;
  message: string;
}

export const SettingsGateCopy: Record<SettingsGateState, ISettingsGateCopy> = {
  [SettingsGateState.CHANGED]: {
    title: 'Calculator Settings Have Changed',
    message: 'Please select which settings to use before calculating payments.',
  },
  [SettingsGateState.UNCHANGED]: {
    title: 'Calculator Settings Unchanged',
    message: "Calculator settings haven't changed since the original payment calculation. Continue to recalculate using these settings.",
  },
  [SettingsGateState.INCOMPLETE]: {
    title: 'Calculating Using Current Settings',
    message: 'Some calculator settings may have changed since the original payment was issued. If you continue, we will calculate using the current site settings.',
  },
};

interface ISettingsGateOption extends ISettingsGateCopy {
  choice: CalculatorSettingsChoice;
  dataCy: string;
}

/** The two option cards on the CHANGED state, in the order the design lists them. */
export const SettingsGateOptions: ISettingsGateOption[] = [
  {
    choice: CalculatorSettingsChoice.ORIGINAL,
    title: 'Original Settings',
    message: 'Use settings from the original calculation.',
    dataCy: 'original-settings-card',
  },
  {
    choice: CalculatorSettingsChoice.CURRENT,
    title: 'Current Settings',
    message: "Use the site's current calculator settings.",
    dataCy: 'current-settings-card',
  },
];

export const SETTINGS_GATE_ACTIONS = { cancel: 'Cancel', calculate: 'Calculate Payments' };

interface UploadSampleTable {
  title: string;
  fileName: string;
  headers: string[];
  data: string[][];
}

export const SAMPLE_UPLOAD_PAYMENT_DATA: UploadSampleTable[] = [
  {
    title: 'Separate First & Last Name',
    fileName: 'kickfin_payments_template_separate_names',
    headers: ['firstName *', 'lastName *', 'amount *', 'cash', 'notes'],
    data: [
      ['Rosemary', 'Fletcher', '55.32', '0.00', ''],
      ['John', '“Smith, III”', '0.00', '85.90', 'Note'],
    ],
  },
  {
    title: 'Consolidated Name',
    fileName: 'kickfin_payments_template_consolidated_names',
    headers: ['name *', 'amount *', 'cash', 'notes'],
    data: [
      ['Rosemary Fletcher', '55.32', '0.00', ''],
      ['“John Smith, III”', '0.00', '85.90', 'Note'],
    ],
  },
  {
    title: 'Employee ID',
    fileName: 'kickfin_payments_template_employee_id',
    headers: ['employeeId *', 'amount *', 'cash', 'notes'],
    data: [
      ['A-123456', '55.32', '0.00', ''],
      ['B-123456', '0.00', '85.90', 'Note'],
    ],
  },
];

// Statuses that are considered valid for processing onboarding employees
export const VALID_STATUS_PROCESS_ONBOARDING_EMPLOYEES: OnboardingEmployeeStatus[] = [
  OnboardingEmployeeStatus.NEW,
  OnboardingEmployeeStatus.LINK,
  OnboardingEmployeeStatus.REACTIVATE,
  OnboardingEmployeeStatus.REACTIVATE_SITE,
  OnboardingEmployeeStatus.ADD_SITE_ROLE,
];
